<template>
  <customModal
    :containermd="true"
    :footerShow="true"
    @closeModal="$emit('close')"
  >
    <template #title>
      <span>Configure Signature Block</span>
    </template>

    <template #body>
      <div class="signature-block-body">
        <div class="signature-block-grid signature-block-grid-order-reason">
          <div class="signature-block-field signature-block-field-order">
            <label for="signature-block-signing-order">Signing Order</label>
            <input
              id="signature-block-signing-order"
              v-model.number="form.signingOrder"
              type="number"
              min="1"
              step="1"
              :class="{ 'is-invalid': errors.signingOrder }"
              @input="errors.signingOrder = ''"
            />
            <small v-if="errors.signingOrder" class="signature-block-error">
              {{ errors.signingOrder }}
            </small>
          </div>

          <div class="signature-block-field">
            <label for="signature-block-reason">Reason for Signing</label>
            <input
              id="signature-block-reason"
              v-model.trim="form.reason"
              type="text"
              placeholder="Enter reason for signing"
              :class="{ 'is-invalid': errors.reason }"
              @change="errors.reason = ''"
            >        
            <small v-if="errors.reason" class="signature-block-error">
              {{ errors.reason }}
            </small>
          </div>
        </div>

        <div class="signature-block-divider"></div>

        <div class="signature-block-section">
          <label class="signature-block-section-label">Signer Assignment</label>

          <div
            class="signature-block-segments"
            role="group"
            aria-label="Signer assignment type"
          >
            <button
              v-for="option in assignmentOptions"
              :key="option.value"
              type="button"
              :class="{ activeoption: form.assignmentType === option.value }"
              @click="setAssignmentType(option.value)"
            >
              {{ option.label }}
            </button>
          </div>

          <p class="signature-block-help">{{ assignmentHelp }}</p>

          <div v-if="showsRoleDropdown" class="signature-block-field">
            <label for="signature-block-role">Role</label>
            <select
              id="signature-block-role"
              v-model="form.role"
              :class="{ 'is-invalid': errors.role }"
              @change="onRoleChange"
              :disabled="loadingRoles"
            >
              <option disabled value="">
                {{ loadingRoles ? "Loading roles..." : "Select role" }}
              </option>
              <option
                v-for="role in resolvedRoleOptions"
                :key="role"
                :value="role"
              >
                {{ role }}
              </option>
            </select>
            <small v-if="errors.role" class="signature-block-error">
              {{ errors.role }}
            </small>
            <small v-if="rolesError" class="signature-block-error">
              {{ rolesError }}
            </small>
          </div>

          <div
            v-if="form.assignmentType === 'usersOnly'"
            class="signature-block-field"
          >
            <label for="signature-block-user">User</label>
            <select
              id="signature-block-user"
              v-model="form.userId"
              :class="{ 'is-invalid': errors.user }"
              @change="errors.user = ''"
              :disabled="loadingUsers"
            >
              <option disabled value="">
                {{ loadingUsers ? "Loading users..." : "Select user" }}
              </option>
              <option
                v-for="user in normalizedUserOptions"
                :key="user.id"
                :value="user.id"
              >
                {{ user.name }} - {{ user.email }}
              </option>
            </select>
            <small v-if="errors.user" class="signature-block-error">
              {{ errors.user }}
            </small>
            <small v-if="usersError" class="signature-block-error">
              {{ usersError }}
            </small>
          </div>

          <div
            v-if="form.assignmentType === 'usersOnly' && selectedUser"
            class="signature-block-signer-list"
          >
            <div class="signature-block-signer">
              <div class="signature-block-avatar">
                {{ getInitials(selectedUser.name) }}
              </div>
              <div class="signature-block-signer-info">
                <strong>{{ selectedUser.name }}</strong>
                <span
                  >{{ selectedUser.email }} - {{ selectedUser.source }} - Site
                  scope: {{ selectedUser.scope }}</span
                >
              </div>
            </div>
          </div>

          <div
            v-if="form.assignmentType === 'roleUsers'"
            class="signature-block-field"
          >
            <label for="signature-block-role-user">User</label>
            <select
              id="signature-block-role-user"
              v-model="form.roleUserId"
              :class="{ 'is-invalid': errors.signers }"
              :disabled="
                loadingRoleUsers || !form.role || !usersForSelectedRole.length
              "
              @change="errors.signers = ''"
            >
              <option disabled value="">
                {{ loadingRoleUsers ? "Loading users..." : "Select user" }}
              </option>
              <option
                v-for="user in usersForSelectedRole"
                :key="user.id"
                :value="user.id"
              >
                {{ user.name }} - {{ user.email }}
              </option>
            </select>
          </div>

          <div
            v-if="form.assignmentType === 'roleUsers'"
            class="signature-block-signer-list"
          >
            <div v-if="selectedRoleUser" class="signature-block-signer">
              <div class="signature-block-avatar">
                {{ getInitials(selectedRoleUser.name) }}
              </div>
              <div class="signature-block-signer-info">
                <strong>{{ selectedRoleUser.name }}</strong>
                <span
                  >{{ selectedRoleUser.email }} -
                  {{ selectedRoleUser.source }} - Site scope:
                  {{ selectedRoleUser.scope }}</span
                >
              </div>
            </div>
            <div v-else-if="loadingRoleUsers" class="signature-block-empty">
              Loading users...
            </div>
            <div
              v-else-if="form.role && !usersForSelectedRole.length"
              class="signature-block-empty"
            >
              No users found for this role.
            </div>
            <div v-else-if="form.role" class="signature-block-empty">
              Select a user for this role.
            </div>
            <div v-else class="signature-block-empty">
              Select a role to view users.
            </div>
          </div>

          <small v-if="errors.signers" class="signature-block-error">
            {{ errors.signers }}
          </small>
        </div>
      </div>
    </template>

    <template #footer>
      <div class="signature-block-footer">
        <button
          type="button"
          class="cancel_btn py-1 px-4"
          @click="$emit('close')"
        >
          Cancel
        </button>
        <button
          type="button"
          class="save_btn py-1 px-4"
          :disabled="saving"
          @click="saveBlock"
        >
          {{ saving ? "Saving..." : "Save Block" }}
        </button>
      </div>
    </template>
  </customModal>
</template>

<script>
import customModal from "../customModal/customModal.vue";
import axios from "axios";
import store from "../../store/index";
const defaultForm = {
  displayName: "",
  signingOrder: 1,
  assignmentType: "roleUsers",
  role: "",
  userId: "",
  roleUserId: "",
};

const defaultUserOptions = [
  {
    id: "",
    name: "",
    email: "",
    source: "Internal",
    scope: "All",
    roles: ["QA Lead"],
  }
];

const defaultSigners = [defaultUserOptions[0]];

export default {
  components: {
    customModal,
  },
  name: "SignatureBlockPopup",
  props: {
    documentName: {
      type: String,
      default: "",
    },
    documentCode: {
      type: String,
      default: "",
    },
    roleOptions: {
      type: Array,
      default: () => [
        "QA Lead",
        "Principal Investigator",
        "Sub Investigator",
        "CRA",
        "Data Manager",
      ],
    },
    userOptions: {
      type: Array,
      default: () => [],
    },
    initialValue: {
      type: Object,
      default: () => null,
    },
    saving: {
      type: Boolean,
      default: false,
    },
  },
  emits: ["close", "save"],
  data() {
    const initialValue = this.initialValue || {};
    const initialForm = initialValue.form || {};
    const initialSigners =
      Array.isArray(initialValue.signers) && initialValue.signers.length
        ? initialValue.signers
        : defaultSigners;
    const initialUserId =
      initialForm.userId || (initialSigners[0] && initialSigners[0].id) || "";
    const initialRoleUserId =
      initialForm.roleUserId ||
      (initialSigners[0] && initialSigners[0].id) ||
      "";

    return {
      roles: [],
      userList: [],
      roleUserList: [],
      roleUserListLoadedFor: "",
      hasLoadedRoles: false,
      hasLoadedUsers: false,
      loadingRoles: false,
      loadingUsers: false,
      loadingRoleUsers: false,
      rolesError: "",
      usersError: "",
      baseurl: process.env.VUE_APP_Service_URL,
      form: {
        ...defaultForm,
        ...initialForm,
        userId: initialUserId || defaultForm.userId,
        roleUserId: initialRoleUserId || defaultForm.roleUserId,
      },
      signers: initialSigners.map((signer) => ({ ...signer })),
      availableInternalSigners: defaultUserOptions.map((signer) => ({
        ...signer,
      })),
      externalInviteCount: 0,
      errors: {
        displayName: "",
        signingOrder: "",
        role: "",
        user: "",
        signers: "",
      },
      assignmentOptions: [
        { label: "Role only", value: "roleOnly" },
        { label: "Specific Users", value: "usersOnly" },
        { label: "Role + Users", value: "roleUsers" },
      ],
    };
  },
  mounted() {
    this.loadSignatureBlockLookups();
  },
  computed: {
    modalSubtitle() {
      const name = this.documentName || "Document";
      return this.documentCode ? `${name} - ${this.documentCode}` : name;
    },
    resolvedRoleOptions() {
      const apiRoles = this.roles
        .map(
          (role) =>
            role && (role.roleName || role.name || role.role_Name || role)
        )
        .filter(Boolean)
        .map((role) => String(role));

      if (apiRoles.length) return apiRoles;

      return this.roleOptions && this.roleOptions.length
        ? this.roleOptions
            .map((role) =>
              typeof role === "string"
                ? role
                : role.roleName || role.name || role.role_Name || ""
            )
            .filter(Boolean)
        : [
            "QA Lead",
            "Principal Investigator",
            "Sub Investigator",
            "CRA",
            "Data Manager",
          ];
    },
    normalizedUserOptions() {
      const users = this.userList.length
        ? this.userList
        : this.userOptions && this.userOptions.length
        ? this.userOptions
        : this.hasLoadedUsers
        ? []
        : defaultUserOptions;

      return users.map((user, index) => this.normalizeUserOption(user, index));
    },
    selectedUser() {
      return (
        this.normalizedUserOptions.find(
          (user) => user.id === this.form.userId
        ) || null
      );
    },
    selectedRoleUser() {
      return (
        this.usersForSelectedRole.find(
          (user) => user.id === this.form.roleUserId
        ) || null
      );
    },
    showsRoleDropdown() {
      return (
        this.form.assignmentType === "roleOnly" ||
        this.form.assignmentType === "roleUsers"
      );
    },
    usersForSelectedRole() {
      if (!this.form.role) return [];

      if (this.roleUserListLoadedFor === this.form.role) {
        return this.roleUserList.map((user, index) =>
          this.normalizeUserOption(user, index)
        );
      }

      return this.normalizedUserOptions.filter((user) =>
        this.userHasRole(user, this.form.role)
      );
    },
    assignmentHelp() {
      if (this.form.assignmentType === "roleOnly") {
        return "Signer must currently hold this role at sign time.";
      }

      if (this.form.assignmentType === "usersOnly") {
        return "Signer must be one of the named users below at sign time.";
      }

      return "Signer must be both named below and currently hold this role. Both checks run at sign time.";
    },
  },
  methods: {
    getAuthHeaders() {
      const idtoken = store.getters.getIdToken;
      return {
        Authorization: "Bearer " + idtoken,
        "Content-Type": "application/json",
      };
    },
    async loadSignatureBlockLookups() {
      await Promise.all([this.getallroles(), this.getuserlist()]);

      if (this.form.assignmentType === "roleUsers" && this.form.role) {
        await this.getuserlist(this.form.role);
      }
    },
    async getallroles() {
      this.loadingRoles = true;
      this.rolesError = "";

      try {
        const res = await axios.get(
          `${this.baseurl}/account-core/roles/rolelist`,
          {
            headers: this.getAuthHeaders(),
          }
        );

        this.roles = Array.isArray(res.data) ? res.data : [];
        this.hasLoadedRoles = true;
      } catch (err) {
        this.roles = [];
        this.rolesError = "Unable to load roles.";
        console.error("Unable to load signature block roles:", err);
      } finally {
        this.loadingRoles = false;
      }
    },
    async getuserlist(roleName = "") {
      const isRoleRequest = !!roleName;
      if (isRoleRequest) {
        this.loadingRoleUsers = true;
      } else {
        this.loadingUsers = true;
      }
      this.usersError = "";

      try {
        const params = {
          pageindex: 1,
          pagesize: 1000,
        };

        if (roleName) {
          params.Role = roleName;
        }

        const res = await axios.get(
          `${this.baseurl}/account-core/user/userlist`,
          {
            params,
            headers: this.getAuthHeaders(),
          }
        );

        const users =
          res.data && Array.isArray(res.data.results) ? res.data.results : [];

        if (isRoleRequest) {
          this.roleUserList = users;
          this.roleUserListLoadedFor = roleName;
          if (!this.selectedRoleUser) {
            this.form.roleUserId = "";
          }
        } else {
          this.userList = users;
          this.hasLoadedUsers = true;
          if (!this.selectedUser) {
            this.form.userId = "";
          }
        }
      } catch (err) {
        if (isRoleRequest) {
          this.roleUserList = [];
          this.roleUserListLoadedFor = roleName;
        } else {
          this.userList = [];
          this.hasLoadedUsers = true;
        }
        this.usersError = "Unable to load users.";
        console.error("Unable to load signature block users:", err);
      } finally {
        if (isRoleRequest) {
          this.loadingRoleUsers = false;
        } else {
          this.loadingUsers = false;
        }
      }
    },
    setAssignmentType(type) {
      this.form.assignmentType = type;
      this.errors.role = "";
      this.errors.user = "";
      this.errors.signers = "";
      if (type === "roleUsers" && this.form.role) {
        this.getuserlist(this.form.role);
      }
    },
    async onRoleChange() {
      this.errors.role = "";
      this.errors.signers = "";

      if (this.form.assignmentType === "roleUsers" && this.form.role) {
        this.form.roleUserId = "";
        await this.getuserlist(this.form.role);
      }
    },
    normalizeUserOption(user, index) {
      const firstName = user.firstName || user.firstname || "";
      const lastName = user.lastName || user.lastname || "";
      const fullName = [firstName, lastName].filter(Boolean).join(" ");
      const email =
        user.email ||
        user.userEmail ||
        user.emailId ||
        user.emailIdPrimary ||
        "";
      const name =
        user.name ||
        user.fullName ||
        user.username ||
        user.userName ||
        fullName ||
        email ||
        `User ${index + 1}`;
      const id = String(user.id || user.userId || user.userID || email || name);

      return {
        ...user,
        id,
        name,
        email,
        source:
          user.source ||
          user.userSource ||
          user.userType ||
          (user.isExternal ? "External" : "Internal"),
        scope: user.scope || user.siteScope || user.siteCode || "All",
        roles: this.getUserRoles(user),
      };
    },
    getUserRoles(user) {
      const roleValues = [];

      if (Array.isArray(user.role)) {
        roleValues.push(...user.role);
      } else {
        roleValues.push(user.role);
      }

      roleValues.push(
        user.roleName,
        user.role_Name,
        user.designation,
        user.designationName,
        user.approvalType
      );

      if (Array.isArray(user.roles)) {
        user.roles.forEach((role) => {
          roleValues.push(
            typeof role === "string"
              ? role
              : role.roleName || role.role_Name || role.name
          );
        });
      }

      if (Array.isArray(user.roleNames)) {
        roleValues.push(...user.roleNames);
      }

      return [
        ...new Set(
          roleValues.filter(Boolean).map((role) => String(role).trim())
        ),
      ];
    },
    userHasRole(user, role) {
      const selectedRole = String(role || "")
        .trim()
        .toLowerCase();
      if (!selectedRole) return false;

      return (user.roles || []).some(
        (userRole) =>
          String(userRole || "")
            .trim()
            .toLowerCase() === selectedRole
      );
    },
    getInitials(name) {
      return (
        String(name || "")
          .split(" ")
          .filter(Boolean)
          .slice(0, 2)
          .map((part) => part.charAt(0).toUpperCase())
          .join("") || "NA"
      );
    },
    removeSigner(index) {
      this.signers.splice(index, 1);
      this.errors.signers = "";
    },
    addInternalSigner() {
      const existingIds = this.signers.map((signer) => signer.id);
      const nextSigner = this.availableInternalSigners.find(
        (signer) => !existingIds.includes(signer.id)
      );

      if (!nextSigner) return;

      this.signers.push({ ...nextSigner });
      this.errors.signers = "";
    },
    inviteExternalSigner() {
      this.externalInviteCount += 1;
      this.signers.push({
        id: `external-${Date.now()}-${this.externalInviteCount}`,
        name: `External signer ${this.externalInviteCount}`,
        email: "external.signer@example.com",
        source: "External",
        scope: "Invitation",
      });
      this.errors.signers = "";
    },
    validateBlock() {
      let isValid = true;
      const signingOrder = Number(this.form.signingOrder);

      this.errors = {
        displayName: "",
        signingOrder: "",
        role: "",
        user: "",
        signers: "",
      };

      if (!this.form.displayName) {
        this.errors.displayName = "Display name is required.";
        isValid = false;
      }

      if (!Number.isFinite(signingOrder) || signingOrder <= 0) {
        this.errors.signingOrder = "Enter a valid signing order.";
        isValid = false;
      }

      if (this.form.assignmentType !== "usersOnly" && !this.form.role) {
        this.errors.role = "Role is required.";
        isValid = false;
      }

      if (this.form.assignmentType === "usersOnly" && !this.selectedUser) {
        this.errors.user = "Please select user.";
        isValid = false;
      }

      if (
        this.form.assignmentType === "roleUsers" &&
        this.form.role &&
        !this.usersForSelectedRole.length
      ) {
        this.errors.signers = "No users found for selected role.";
        isValid = false;
      }

      if (
        this.form.assignmentType === "roleUsers" &&
        this.usersForSelectedRole.length &&
        !this.selectedRoleUser
      ) {
        this.errors.signers = "Please select user.";
        isValid = false;
      }

      return isValid;
    },
    getSelectedSigners() {
      if (this.form.assignmentType === "roleOnly") return [];
      if (this.form.assignmentType === "usersOnly") {
        return this.selectedUser ? [this.selectedUser] : [];
      }

      return this.selectedRoleUser ? [this.selectedRoleUser] : [];
    },
    getSelectedRoleId() {
      const roleName = String(this.form.role || "")
        .trim()
        .toLowerCase();
      const role = this.roles.find((item) => {
        const name =
          item && (item.roleName || item.name || item.role_Name || item);
        return (
          String(name || "")
            .trim()
            .toLowerCase() === roleName
        );
      });
      if (role && typeof role === "object") {
        return role.roleName || role.name || role.role_Name || null;
      }
      console.log("selectedRoleUser", this.selectedRoleUser);
      return (
        (this.selectedRoleUser &&
          (this.selectedRoleUser.roleName || this.selectedRoleUser.roleName)) ||
        null
      );
    },
    saveBlock() {
      if (!this.validateBlock()) return;

      this.$emit("save", {
        form: {
          ...this.form,
          signingOrder: Number(this.form.signingOrder),
          role: this.getSelectedRoleId(),
        },
        signers: this.getSelectedSigners().map((signer) => ({ ...signer })),
      });
    },
  },
};
</script>

<style scoped>
.signature-block-body {
  max-height: calc(100vh - 220px);
  overflow-y: auto;
  padding: 8px 0;
}

.signature-block-grid {
  display: grid;
  gap: 14px;
  grid-template-columns: repeat(2, minmax(0, 1fr));
}

.signature-block-grid-order-reason {
  grid-template-columns: 140px minmax(0, 1fr);
}

.signature-block-field label,
.signature-block-section-label {
  color: #111827;
  display: block;
  font-size: 16px;
  font-weight: 600;
  line-height: 1.25;
  margin-bottom: 8px;
}

.signature-block-field input,
.signature-block-field select {
  background: #fff;
  border: 1px solid #d8dce2;
  border-radius: 8px;
  color: #030712;
  font-size: 13px;
  height: 30px;
  line-height: 1.3;
  outline: 0;
  padding: 4px 16px;
  width: 100%;
}

.signature-block-field input:focus,
.signature-block-field select:focus {
  border-color: #60a5fa;
  box-shadow: 0 0 0 3px rgba(96, 165, 250, 0.2);
}

.signature-block-field .is-invalid {
  border-color: #dc2626;
}

.signature-block-divider {
  background: #e5e7eb;
  height: 1px;
  margin: 22px 0 20px;
}

.signature-block-section {
  display: flex;
  flex-direction: column;
  gap: 10px;
}

.signature-block-segments {
  display: grid;
  gap: 10px;
  grid-template-columns: repeat(3, minmax(0, 1fr));
}

.signature-block-segments button,
.signature-block-actions button {
  align-items: center;
  background: #fff;
  border: 1px solid #cfd4dc;
  border-radius: 8px;
  color: #111827;
  display: inline-flex;
  font-size: 15px;
  font-weight: 600;
  gap: 8px;
  justify-content: center;
  min-height: 30px;
  padding: 4px 12px;
  text-align: center;
}

.signature-block-segments button.activeoption {
  background: #cfe3ff;
  border-color: #76a9fa;
  color: #08469b;
}

.signature-block-help {
  color: #1f2937;
  font-size: 15px;
  line-height: 1.35;
  margin: 2px 0 4px;
}

.signature-block-signer-list {
  display: flex;
  flex-direction: column;
  gap: 8px;
}

.signature-block-signer {
  align-items: center;
  background: #fbfbfc;
  border-radius: 8px;
  display: grid;
  gap: 12px;
  grid-template-columns: 42px minmax(0, 1fr);
  min-height: 64px;
  padding: 10px 14px;
}

.signature-block-empty {
  background: #fbfbfc;
  border: 1px dashed #cfd4dc;
  border-radius: 8px;
  color: #657184;
  font-size: 14px;
  line-height: 1.4;
  padding: 12px 14px;
}

.signature-block-avatar {
  align-items: center;
  background: #cfe3ff;
  border-radius: 50%;
  color: #08469b;
  display: inline-flex;
  font-size: 15px;
  font-weight: 700;
  height: 40px;
  justify-content: center;
  width: 40px;
}

.signature-block-signer-info {
  display: flex;
  flex-direction: column;
  min-width: 0;
}

.signature-block-signer-info strong {
  color: #030712;
  font-size: 16px;
  line-height: 1.25;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.signature-block-signer-info span {
  color: #657184;
  font-size: 14px;
  line-height: 1.3;
  overflow-wrap: anywhere;
}

.signature-block-remove {
  align-items: center;
  background: transparent;
  border: 0;
  color: #7c8797;
  display: inline-flex;
  height: 34px;
  justify-content: center;
  padding: 0;
  width: 34px;
}

.signature-block-remove:hover {
  color: #b42318;
}

.signature-block-actions {
  display: grid;
  gap: 10px;
  grid-template-columns: repeat(2, minmax(0, 1fr));
}

.signature-block-actions button:hover,
.signature-block-segments button:hover {
  border-color: #86b7fe;
}

.signature-block-error {
  color: #dc2626;
  display: block;
  font-size: 12px;
  margin-top: 5px;
}

.signature-block-footer {
  align-items: center;
  display: flex;
  gap: 10px;
  justify-content: flex-end;
}

.signature-block-secondary,
.signature-block-primary {
  border-radius: 8px;
  font-size: 15px;
  font-weight: 600;
  min-height: 38px;
  min-width: 100px;
  padding: 8px 16px;
}

.signature-block-secondary {
  background: #fff;
  border: 1px solid #cfd4dc;
  color: #111827;
}

.signature-block-primary {
  background: #145faf;
  border: 1px solid #145faf;
  color: #fff;
}

@media (max-width: 640px) {
  .signature-block-grid,
  .signature-block-segments,
  .signature-block-actions {
    grid-template-columns: 1fr;
  }

  .signature-block-footer {
    align-items: stretch;
    flex-direction: column-reverse;
  }

  .signature-block-secondary,
  .signature-block-primary {
    width: 100%;
  }
}
</style>